import React, { useEffect, useReducer } from "react";
import { graphql, useFragment } from "react-relay";

import { faCalendar, faEllipsisV, faSignalStream } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";
import { commitMutation, fetchQuery } from "relay-runtime";

import type { EventFormResult } from "~/calendar/EventForm";
import { confirm } from "~/lib/prompts";
import { useRecordIconShouldBeVisible } from "~/meetingRecorder/RecordIcon";
import { environment } from "~/models";
import MenuLink, { type MenuLinkProps } from "~/nav/panels/Home/Links/MenuLink";
import { HideEventMutation, invalidateHiddenEvent } from "~/nav/panels/Meetings/Events";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import { useModals } from "~/ui";

import EventFormModal from "../../Meetings/EventFormModal/EventFormModal";
import GroupsMenu from "./GroupsMenu";

import type { Events_HideEventMutation as HideEventMutationQuery } from "~/__generated__/Events_HideEventMutation.graphql";
import type { MeetingLink_meeting$key as MeetingFragment } from "~/__generated__/MeetingLink_meeting.graphql";
import type { MeetingLinkEventModalQuery } from "~/__generated__/MeetingLinkEventModalQuery.graphql";

import styles from "~/nav/panels/shared/PanelSection.module.css";

const calendarIcon = <FontAwesomeIcon icon={faCalendar} fixedWidth />;
const signalStreamIcon = <FontAwesomeIcon icon={faSignalStream} />;

const formatDate = (date: string | null | undefined, isAllDay: boolean): string | null => {
	if (!date) return null;

	if (isAllDay) {
		return "All day";
	} else {
		return moment(date).format("h:mm a");
	}
};

export default function MeetingLink(
	props: Omit<MenuLinkProps, "to" | "innerRef" | "activeClassName" | "streamId"> & {
		refetchMeetingDataFunc: () => void;
		meeting: MeetingFragment;
		innerRef?: React.Ref<HTMLAnchorElement>;
		disabled?: boolean;
	},
) {
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const toggleOpen = React.useCallback((open: boolean) => setIsMenuOpen(open), []);
	const modals = useModals();

	const { refetchMeetingDataFunc, meeting: meetingProp, innerRef, className, ...restProps } = props;

	const meeting = useFragment(
		graphql`
			fragment MeetingLink_meeting on Event {
				id
				title
				guid
				isHidden
				isOrganizer
				start
				end
				isAllDay
				note {
					finished
					stream {
						...GroupsMenu_stream
						id
						viewerLastSeenAt
						latestNoteEditedAt
						hasExternalUsersOrHasRelatedExternalStreams
					}
				}
				type
				calendar {
					id
				}
				...RecordIcon_event
			}
		`,
		meetingProp,
	);

	const hideMeeting = React.useCallback(() => {
		const message = meeting.isHidden ? (
			"Showing a recurring event will also show all events in the series."
		) : (
			<>
				<span>
					You can reveal this event by selecting <i>Show hidden meetings</i> in the filters menu&ensp;
					<FontAwesomeIcon icon={faEllipsisV} size="1x" />
					&ensp;at the top of the meetings panel.
				</span>
				<br />
				<br />
				<span>Hiding a recurring event will also hide all events in the series.</span>
			</>
		);

		confirm(message, {
			yesText: meeting.isHidden ? "Show" : "Hide",
			title: meeting.isHidden ? "Show selected event again?" : "Hide selected event?",
		}).then(result => {
			if (!result) {
				return;
			}

			commitMutation<HideEventMutationQuery>(environment, {
				mutation: HideEventMutation,
				variables: { guid: meeting.guid },
				onCompleted: ({ hideEvent }) => {
					if (hideEvent?.ok) {
						invalidateHiddenEvent(meeting.id);
						refetchMeetingDataFunc();
					}
				},
			});
		});
	}, [meeting.guid, meeting.id, meeting.isHidden, refetchMeetingDataFunc]);

	const onFormFinish = React.useCallback(
		(closeModal: () => void) => (result: EventFormResult) => {
			switch (result.type) {
				case "succeeded": {
					invalidateHiddenEvent(meeting.id);
					refetchMeetingDataFunc();
					break;
				}
				case "deleted": {
					refetchMeetingDataFunc();
					break;
				}
				case "failed": {
					return;
				}
				default: {
					break;
				}
			}

			closeModal();
		},
		[meeting.id, refetchMeetingDataFunc],
	);

	const editEvent = React.useCallback(async () => {
		const response = await fetchQuery<MeetingLinkEventModalQuery>(
			environment,
			graphql`
				query MeetingLinkEventModalQuery {
					viewer {
						id
						...EventFormModal_viewer
					}
				}
			`,
			{},
			{ fetchPolicy: "store-or-network" },
		).toPromise();

		if (!response) return;

		modals.open(({ close }) => (
			<React.Suspense fallback={null}>
				<EventFormModal
					onFormFinish={onFormFinish(close)}
					viewer={response.viewer}
					eventToEdit={{
						guid: meeting.guid,
						title: meeting.title,
						start: meeting.start ? new Date(meeting.start) : new Date(),
						end: meeting.end ? new Date(meeting.end) : new Date(),
						isAllDay: meeting.isAllDay,
						type: meeting.type,
						calendar: meeting.calendar,
					}}
				/>
			</React.Suspense>
		));
	}, [onFormFinish, meeting, modals]);

	// Hook to dynamically update meeting "in progress" status
	const [updated, forceUpdate] = useReducer(x => x + 1, 0);
	useEffect(() => {
		if (!meeting.start || !meeting.end || meeting.note?.finished || moment().isAfter(meeting.end)) {
			return;
		}

		// Re-render component to check progress status every 60 seconds
		// Repeated checks needed to prevent wrong timeout if computer sleeps (https://stackoverflow.com/a/6347336/5594265)
		const inProgressInterval = setInterval(forceUpdate, 60000);
		return () => clearInterval(inProgressInterval);
	}, [meeting, updated]);

	const { id, guid, title, start, end, note, isAllDay } = meeting;

	const inProgress = start && end && moment().isBetween(start, end) && note?.finished !== true;

	const unread = Boolean(
		note?.stream.latestNoteEditedAt && moment(note?.stream.viewerLastSeenAt).isBefore(note?.stream.latestNoteEditedAt),
	);

	const isNoteStreamConnected = note?.stream.hasExternalUsersOrHasRelatedExternalStreams ?? false;
	const recordIconShouldBeVisible = useRecordIconShouldBeVisible(meeting);

	return (
		<div className={cx(styles.wrapper, { [styles.disabled]: props.disabled })}>
			<MenuLink
				{...restProps}
				innerRef={innerRef}
				to={`/meetings/${guid}/`}
				className={className}
				activeClassName={styles.active}
				showRecordIcon={recordIconShouldBeVisible}
				icon={calendarIcon}
				iconTooltip={recordIconShouldBeVisible ? "This meeting will be recorded" : "Meeting"}
				unread={unread}
				streamId={note?.stream.id}
			>
				<span title={title} className={styles.title}>
					{isNoteStreamConnected && <ConnectedIcon className={styles.connectedIcon} />}
					{title}
				</span>
				{!isMenuOpen && (
					<span className={cx(styles.date, { [styles.inProgress]: inProgress })}>
						{inProgress && signalStreamIcon}
						{formatDate(start, isAllDay)}
					</span>
				)}
			</MenuLink>
			<GroupsMenu
				eventId={id}
				stream={note?.stream}
				hideMeeting={hideMeeting}
				editEvent={meeting.isOrganizer ? editEvent : undefined}
				isMenuOpen={isMenuOpen}
				toggleOpen={toggleOpen}
			/>
		</div>
	);
}
